import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { EmdHierarchicalGridComponent } from '../charts/emd-hierarchical-grid/emd-hierarchical-grid.component';
import { EmdFilterService } from '../emd-filter.service';
import { EmdSelectionService } from '../emd-selection.service';
import {
  DETAILS_DEFAULT,
  EMD_COLORS,
  EmdDetailsData,
  EmdTrendBucket,
  WHOLESALER_COLORS,
  deriveDetails,
  wholesalerColor,
} from '../emd-mock-data';

/** Details tab: a multi-series sales trend and an expandable Sales Overview grid. */
@Component({
    selector: 'emd-details-tab',
    templateUrl: './emd-details-tab.component.html',
    styleUrls: ['./emd-details-tab.component.scss'],
    standalone: false
})
export class EmdDetailsTabComponent implements OnInit, OnChanges, OnDestroy {
  /** The card header owns the search box, so the quick filter is driven here. */
  @ViewChild(EmdHierarchicalGridComponent) grid?: EmdHierarchicalGridComponent;

  /** W/M/Q/Y time bucket for the trend, driven by the shell's tab-strip pills. */
  @Input() bucket: EmdTrendBucket = 'M';

  readonly colors = EMD_COLORS;

  /** Latest derivation; the trend and the grid both read out of this. */
  data: EmdDetailsData = DETAILS_DEFAULT;

  series = this.data.series;
  groupBy = 'Wholesaler';
  groupModes = ['Wholesaler', 'Material'];
  unitsToggle = 'units';

  /** Metric label shown in the trend tooltip, following the Units/$ toggle. */
  get trendValueLabel(): string {
    return this.unitsToggle === '$' ? 'Sales, $' : 'Quantity Shipped';
  }

  overviewMode = 'Sales';
  overviewModes = ['Sales', 'Returns'];
  searchText = '';

  // ── Sales trend ─────────────────────────────────────────────────────────────

  trendData: { period: string; series: string; value: number }[] = [];

  /** Every wholesaler, not just the ones currently drawn, so a badge cut can
   *  reintroduce any of them without the colour map being rebuilt. */
  readonly trendColors = WHOLESALER_COLORS;

  // ── Sales Overview grid ─────────────────────────────────────────────────────

  overviewRows = this.data.rows;
  overviewColumnDefs: any[] = [];
  overviewChildColumnDefs: any[] = [];

  /** Cross-filter selection, so the grid dims alongside the other tabs' visuals. */
  selectedWholesalers: string[] = [];

  private readonly subs: Subscription[] = [];

  constructor(
    private filters: EmdFilterService,
    private selection: EmdSelectionService
  ) {}

  ngOnInit(): void {
    this.subs.push(
      this.filters.changes.subscribe((tab) => {
        if (tab === 'Details') { this.refresh(); }
      })
    );
    this.subs.push(
      this.selection.changes.subscribe(() => {
        // Effective = pending while staging, else committed. New array
        // reference, so ngOnChanges fires on the grid.
        this.selectedWholesalers = [...this.selection.effective('wholesaler')];
      })
    );
    this.refresh();
  }

  /** React to the shell's time-bucket pills (re-derive the trend at the new bucket). */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['bucket'] && !changes['bucket'].firstChange) {
      this.refresh();
    }
  }

  onRowSelect(event: { wholesaler: string; ctrlKey: boolean }): void {
    this.selection.stage('wholesaler', event.wholesaler, 'details-grid');
  }

  /** A trend line is one wholesaler — clicking it stages that wholesaler.
   *  Guarded to Wholesaler grouping; Material series are not a cut dimension. */
  onSeriesClick(event: { series: string; ctrlKey: boolean }): void {
    if (this.groupBy !== 'Wholesaler') { return; }
    this.selection.stage('wholesaler', event.series, 'details-trend');
  }

  /** Units/$ toggle in the trend header — swaps the plotted metric in place. */
  onUnitsToggle(value: string): void {
    this.unitsToggle = value;
    this.buildTrendData();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  /** Re-aggregate from the fact tables for the current badge filters. A
   *  confirmed selection lands in the Wholesaler badge, so it is included here. */
  private refresh(): void {
    const d = deriveDetails(this.filters.filter('Details'), this.bucket);
    this.data = d;
    this.series = d.series;
    this.overviewRows = d.rows;

    // Date columns move with the reporting window, so the column defs are
    // rebuilt rather than only the rows swapped.
    this.buildGridColumns();
    this.buildTrendData();
  }

  /** Flatten the current derivation to the chart's shape for the active
   *  Units/$ toggle. Split out so the toggle can rebuild without re-deriving. */
  private buildTrendData(): void {
    const inUsd = this.unitsToggle === '$';
    this.trendData = this.data.trend.map((x) => ({
      period: x.period,
      series: x.wholesaler,
      value: inUsd ? x.sales_usd : x.sales_qty,
    }));
  }

  colorFor(name: string): string {
    return wholesalerColor(name);
  }

  onSearch(term: string): void {
    this.searchText = term;
    this.grid?.onQuickFilterChanged(term);
  }

  private buildGridColumns(): void {
    const dateCols = this.data.dateColumns.map((label, i) => ({
      headerName: label,
      field: `d${i}`,
      type: 'Number',
      minWidth: 130,
      flex: 1,
      valueFormatter: (p: any) => (p.value ?? '').toLocaleString('en-US'),
    }));

    this.overviewColumnDefs = [
      {
        headerName: 'Wholesaler',
        field: 'wholesaler',
        cellRenderer: 'agGroupCellRenderer',
        pinned: 'left',
        minWidth: 220,
        flex: 1.4,
        cellClass: 'emd-grid-parent-cell',
      },
      {
        headerName: 'Total Sales',
        field: 'total',
        type: 'Number',
        minWidth: 120,
        flex: 1,
        cellClass: 'emd-grid-total-cell',
        valueFormatter: (p: any) => (p.value ?? '').toLocaleString('en-US'),
      },
      ...dateCols,
    ];

    this.overviewChildColumnDefs = [
      {
        // Child rows are the ship-to accounts under each wholesaler.
        headerName: 'Customer',
        field: 'wholesaler',
        colId: 'wholesaler',
        pinned: 'left',
        minWidth: 220,
        flex: 1.4,
        cellClass: 'emd-grid-child-cell',
      },
      {
        headerName: 'Total Sales',
        field: 'total',
        colId: 'total',
        minWidth: 120,
        flex: 1,
      },
      ...this.data.dateColumns.map((label, i) => ({
        headerName: label,
        field: `d${i}`,
        colId: `d${i}`,
        minWidth: 130,
        flex: 1,
      })),
    ];
  }
}
